<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { 
  NCard,
  NButton,
  NSelect,
  NForm,
  NFormItem,
  NInput,
  NAlert,
  NDivider,
  NSpace,
  NSpin,
  NIcon,
  NModal
} from 'naive-ui';
import { 
  Key,
  Shield,
  Eye,
  EyeOff,
  Save
} from 'lucide-vue-next';

import { updateLanguage, authToken, isPatient } from '@/services/auth';
import { LANGUAGES, getPreferredLanguage, savePreferredLanguage, getLanguageLabel } from '@/services/language';
import { fetchMyPatientProfile, recoverPatientPassword, updatePatientProfile } from '@/services/patient';
import { buildQuestionHashMap, getQuestionFromHash, verifySecurityAnswer, 
  derivePasswordFromSecurity, decryptPatientData, encryptPatientData } from '@/services/encryption';

// Language settings (existing)
const currentLanguage = ref(getPreferredLanguage() || 'english');
const selectedLanguage = ref(currentLanguage.value);
const isLoading = ref(false);
const errorMessage = ref('');
const successMessage = ref('');

// Patient recovery state
const patientData = ref(null);
const recoveryData = ref(null);
const loadingPatientData = ref(false);
const recoveryAnswers = ref({
  answer1: "",
  answer2: "",
  answer3: ""
});
const recoveryError = ref(null);
const isRecovering = ref(false);
const showPasswordRecoveryModal = ref(false);

// New password setup for recovery
const newPasswordSetup = ref({
  password: '',
  confirmPassword: ''
});
const showNewPassword = ref(false);
const showConfirmNewPassword = ref(false);
const isSettingNewPassword = ref(false);

// Recovery attempts tracking
const recoveryAttempts = ref(0);
const isBlocked = ref(false);
const blockExpiresAt = ref(null);
const timeRemaining = ref(0);

// Question hash mapping
const questionHashMap = ref({});

// Function to get readable question from hash
const getQuestionText = (questionHash) => {
  return getQuestionFromHash(questionHash, questionHashMap.value);
};

// Compute if there are changes to save
const hasChanged = computed(() => {
  return selectedLanguage.value !== currentLanguage.value;
});

// Check if new password is valid
const isNewPasswordValid = computed(() => {
  return newPasswordSetup.value.password.length >= 8 &&
    newPasswordSetup.value.password === newPasswordSetup.value.confirmPassword;
});

// Check if currently blocked and update timer
const checkBlockStatus = () => {
  if (!blockExpiresAt.value) {
    isBlocked.value = false;
    timeRemaining.value = 0;
    return;
  }

  const now = Date.now();
  if (now >= blockExpiresAt.value) {
    isBlocked.value = false;
    blockExpiresAt.value = null;
    timeRemaining.value = 0;
    recoveryAttempts.value = 0;
    localStorage.removeItem('recovery_attempts');
    localStorage.removeItem('block_expires_at');
  } else {
    isBlocked.value = true;
    timeRemaining.value = Math.ceil((blockExpiresAt.value - now) / 1000);
  }
};

// Format time remaining
const formatTimeRemaining = computed(() => {
  const minutes = Math.floor(timeRemaining.value / 60);
  const seconds = timeRemaining.value % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
});

// Load recovery attempt data from localStorage
const loadRecoveryState = () => {
  const attempts = localStorage.getItem('recovery_attempts');
  const blockExpires = localStorage.getItem('block_expires_at');
  
  if (attempts) {
    recoveryAttempts.value = parseInt(attempts);
  }
  
  if (blockExpires) {
    blockExpiresAt.value = parseInt(blockExpires);
    checkBlockStatus();
  }
};

// Save recovery attempt data to localStorage
const saveRecoveryState = () => {
  localStorage.setItem('recovery_attempts', recoveryAttempts.value.toString());
  if (blockExpiresAt.value) {
    localStorage.setItem('block_expires_at', blockExpiresAt.value.toString());
  }
};

// Increment failed attempts
const incrementFailedAttempts = () => {
  recoveryAttempts.value++;
  
  if (recoveryAttempts.value >= 3) {
    blockExpiresAt.value = Date.now() + (10 * 60 * 1000);
    isBlocked.value = true;
    recoveryError.value = "Too many failed attempts. Please try again in 10 minutes.";
  }
  
  saveRecoveryState();
};

// Reset attempts on successful recovery
const resetRecoveryAttempts = () => {
  recoveryAttempts.value = 0;
  isBlocked.value = false;
  blockExpiresAt.value = null;
  timeRemaining.value = 0;
  localStorage.removeItem('recovery_attempts');
  localStorage.removeItem('block_expires_at');
};

// Load patient data for recovery questions
const loadPatientData = async () => {
  if (!isPatient.value) return;
  
  try {
    loadingPatientData.value = true;
    const profileData = await fetchMyPatientProfile(authToken.value);
    
    if (profileData) {
      patientData.value = profileData;
    }
  } catch (err) {
    console.error('Failed to load patient data:', err);
  } finally {
    loadingPatientData.value = false;
  }
};

// Get recovery data from backend
const getRecoveryData = async () => {
  try {
    const response = await recoverPatientPassword(authToken.value);
    recoveryData.value = response;
    return response;
  } catch (err) {
    console.error('Failed to get recovery data:', err);
    throw err;
  }
};

// Verify answers and get the old password
const verifyAnswersForRecovery = async (answers, recoveryData) => {
  try {
    const valid1 = await verifySecurityAnswer(answers.answer1, recoveryData.security_answer_1_hash);
    const valid2 = await verifySecurityAnswer(answers.answer2, recoveryData.security_answer_2_hash);
    const valid3 = await verifySecurityAnswer(answers.answer3, recoveryData.security_answer_3_hash);

    if (!(valid1 && valid2 && valid3)) {
      return { success: false, error: "Incorrect security answers" };
    }

    // ✅ FIXED: Recover the old PASSWORD using security answers
    const securityKey = await derivePasswordFromSecurity([
      answers.answer1, 
      answers.answer2, 
      answers.answer3
    ]);

    // Decrypt the stored password (not master key!)
    const oldPassword = await decryptPatientData(
      recoveryData.encrypted_recovery_password,
      recoveryData.recovery_encryption_metadata,
      securityKey
    );

    return { success: true, oldPassword: oldPassword };
  } catch (err) {
    console.error('Answer verification failed:', err);
    return { success: false, error: err.message };
  }
};

// ✅ FIXED: Re-encrypt with new password (password-only approach)
const reEncryptWithNewPassword = async (oldPassword, newPassword, securityAnswers) => {
  try {
    // First decrypt the patient data with the OLD password
    const decryptedPatientData = await decryptPatientData(
      patientData.value.encrypted_patient_data,
      patientData.value.encryption_metadata,
      oldPassword  // Using old password only
    );

    // Re-encrypt patient data with NEW password only
    const { encryptedData, encryptionMetadata } = await encryptPatientData(
      decryptedPatientData,
      newPassword  // Using new password only
    );

    // Encrypt the new password with security answers for future recovery
    const securityKey = await derivePasswordFromSecurity([
      securityAnswers.answer1,
      securityAnswers.answer2,
      securityAnswers.answer3
    ]);

    const { encryptedData: recoveryEncryptedKey, encryptionMetadata: recoveryKeyMetadata } = await encryptPatientData(
      newPassword,  // Encrypting the password itself
      securityKey
    );

    return {
      encrypted_patient_data: encryptedData,
      encryption_metadata: encryptionMetadata,
      encrypted_recovery_password: recoveryEncryptedKey,
      recovery_encryption_metadata: recoveryKeyMetadata
    };
  } catch (err) {
    console.error('Re-encryption failed:', err);
    throw err;
  }
};

// Save the language preference
async function saveLanguagePreference() {
  if (!hasChanged.value) return;
  
  errorMessage.value = '';
  successMessage.value = '';
  isLoading.value = true;
  
  try {
    const result = await updateLanguage(authToken.value, selectedLanguage.value);
    currentLanguage.value = selectedLanguage.value;
    savePreferredLanguage(selectedLanguage.value);
    successMessage.value = 'Language preference updated successfully!';
  } catch (error) {
    console.error('Failed to update language:', error);
    errorMessage.value = error.message || 'Failed to update language preference. Please try again.';
  } finally {
    isLoading.value = false;
  }
}

// Start password recovery process
const startPasswordRecovery = async () => {
  if (isBlocked.value) {
    recoveryError.value = `Recovery is blocked. Please try again in ${formatTimeRemaining.value}.`;
    return;
  }

  if (!recoveryAnswers.value.answer1.trim() || 
      !recoveryAnswers.value.answer2.trim() || 
      !recoveryAnswers.value.answer3.trim()) {
    recoveryError.value = "Please answer all security questions";
    return;
  }

  try {
    isRecovering.value = true;
    recoveryError.value = null;

    const backendData = await getRecoveryData();
    const result = await verifyAnswersForRecovery(recoveryAnswers.value, backendData);

    if (result.success) {
      resetRecoveryAttempts();
      // Store the old password for re-encryption
      recoveryData.value.oldPassword = result.oldPassword;
      showPasswordRecoveryModal.value = true;
    } else {
      incrementFailedAttempts();
      recoveryError.value = result.error;
    }

  } catch (err) {
    console.error('Password recovery failed:', err);
    incrementFailedAttempts();
    recoveryError.value = err.message || "Recovery failed. Please try again.";
  } finally {
    isRecovering.value = false;
  }
};

// Complete password recovery with new password
const completePasswordRecovery = async () => {
  if (!isNewPasswordValid.value) {
    recoveryError.value = "Please enter a valid password";
    return;
  }

  try {
    isSettingNewPassword.value = true;
    recoveryError.value = null;

    // Use the old password to decrypt and new password to re-encrypt
    const reEncryptedData = await reEncryptWithNewPassword(
      recoveryData.value.oldPassword,  // Old password
      newPasswordSetup.value.password,  // New password
      recoveryAnswers.value  // Security answers
    );

    await updatePatientProfile(authToken.value, reEncryptedData);

    showPasswordRecoveryModal.value = false;
    successMessage.value = "Password updated successfully! You can now use your new password to access your profile.";
    
    recoveryAnswers.value = { answer1: "", answer2: "", answer3: "" };
    newPasswordSetup.value = { password: '', confirmPassword: '' };
    recoveryData.value = null;

  } catch (err) {
    console.error('Failed to update password:', err);
    recoveryError.value = err.message || "Failed to update password. Please try again.";
  } finally {
    isSettingNewPassword.value = false;
  }
};

const cancelPasswordRecovery = () => {
  showPasswordRecoveryModal.value = false;
  newPasswordSetup.value = { password: '', confirmPassword: '' };
  recoveryError.value = null;
};

// Timer interval
let timerInterval = null;

// Start timer if blocked
const startTimer = () => {
  if (timerInterval) clearInterval(timerInterval);
  
  timerInterval = setInterval(() => {
    checkBlockStatus();
    
    if (!isBlocked.value && timerInterval) {
      clearInterval(timerInterval);
      timerInterval = null;
    }
  }, 1000);
};

// Load current preferences when component mounts
onMounted(async () => {
  selectedLanguage.value = getPreferredLanguage() || 'english';
  loadRecoveryState();
  
  if (isBlocked.value) {
    startTimer();
  }
  
  questionHashMap.value = await buildQuestionHashMap();
  
  if (isPatient.value) {
    loadPatientData();
  }
});

// Cleanup timer on unmount
onBeforeUnmount(() => {
  if (timerInterval) {
    clearInterval(timerInterval);
  }
});
</script>

<template>
  <div class="settings-container">
    <h1>Settings</h1>
    
    <!-- Language Preference Section -->
    <NCard title="Language Preference" class="settings-section">
      <NForm>
        <NFormItem label="Select your preferred language">
          <NSelect
            v-model:value="selectedLanguage"
            :options="LANGUAGES"
            :disabled="isLoading"
            placeholder="Choose language"
          />
        </NFormItem>
        
        <div v-if="errorMessage" class="error-message">
          <NAlert type="error" :title="errorMessage" closable />
        </div>
        
        <div v-if="successMessage" class="success-message">
          <NAlert type="success" :title="successMessage" closable />
        </div>
        
        <NButton 
          @click="saveLanguagePreference" 
          :loading="isLoading"
          :disabled="!hasChanged"
          type="primary"
        >
          Save Changes
        </NButton>
      </NForm>
    </NCard>

    <!-- New Password Modal -->
    <NModal v-model:show="showPasswordRecoveryModal" preset="card" title="Set New Password" :closable="false"
      :mask-closable="false" style="width: 500px">
      <div class="password-modal-content">
        <NAlert type="success" title="Security Questions Verified" class="verification-success">
          Your identity has been verified. Please set a new password for your patient profile.
        </NAlert>

        <NForm>
          <NFormItem label="New Password (minimum 8 characters)" required>
            <NInput v-model:value="newPasswordSetup.password" :type="showNewPassword ? 'text' : 'password'"
              placeholder="Enter a strong password" :disabled="isSettingNewPassword">
              <template #suffix>
                <NButton text @click="showNewPassword = !showNewPassword" :disabled="isSettingNewPassword">
                  <NIcon>
                    <Eye v-if="!showNewPassword" />
                    <EyeOff v-else />
                  </NIcon>
                </NButton>
              </template>
            </NInput>
          </NFormItem>

          <NFormItem label="Confirm New Password" required>
            <NInput v-model:value="newPasswordSetup.confirmPassword" :type="showConfirmNewPassword ? 'text' : 'password'"
              placeholder="Confirm your password" :disabled="isSettingNewPassword">
              <template #suffix>
                <NButton text @click="showConfirmNewPassword = !showConfirmNewPassword" :disabled="isSettingNewPassword">
                  <NIcon>
                    <Eye v-if="!showConfirmNewPassword" />
                    <EyeOff v-else />
                  </NIcon>
                </NButton>
              </template>
            </NInput>
          </NFormItem>

          <div v-if="newPasswordSetup.password && newPasswordSetup.confirmPassword && !isNewPasswordValid"
            class="password-error">
            <NAlert type="error" title="Passwords don't match or are too short" />
          </div>
        </NForm>

        <div v-if="recoveryError" class="error-message">
          <NAlert type="error" :title="recoveryError" closable />
        </div>
      </div>

      <template #footer>
        <NSpace justify="end">
          <NButton @click="cancelPasswordRecovery" :disabled="isSettingNewPassword">
            Cancel
          </NButton>
          <NButton type="primary" @click="completePasswordRecovery" :loading="isSettingNewPassword"
            :disabled="!isNewPasswordValid">
            <template #icon>
              <NIcon>
                <Save />
              </NIcon>
            </template>
            Update Password
          </NButton>
        </NSpace>
      </template>
    </NModal>

    <!-- Patient Password Recovery Section -->
    <NCard v-if="isPatient" title="Patient Password Recovery" class="settings-section">
      <template #header-extra>
        <NIcon>
          <Shield />
        </NIcon>
      </template>
      
      <div v-if="loadingPatientData" class="loading-state">
        <NSpin size="medium" />
        <p>Loading patient data...</p>
      </div>
      
      <div v-else-if="!patientData">
        <NAlert type="info" title="No Patient Profile">
          You don't have a patient profile yet. Create a patient profile to enable password recovery.
        </NAlert>
      </div>
      
      <div v-else>
        <p class="recovery-description">
          If you've forgotten your patient password, answer your security questions below to set a new password.
        </p>
        
        <!-- Block notice -->
        <div v-if="isBlocked" class="block-notice">
          <NAlert type="error" title="Too Many Failed Attempts">
            Recovery is temporarily blocked due to too many failed attempts. 
            Please try again in {{ formatTimeRemaining }}.
          </NAlert>
        </div>
        
        <!-- Attempts warning -->
        <div v-else-if="recoveryAttempts > 0" class="attempts-warning">
          <NAlert type="warning" :title="`Attempt ${recoveryAttempts}/3`">
            {{ 3 - recoveryAttempts }} attempts remaining before recovery is blocked for 10 minutes.
          </NAlert>
        </div>
        
        <NAlert type="info" title="Password Recovery Process" class="security-notice">
          After verifying your security questions, you'll be able to set a new password. This will securely re-encrypt your data with the new password.
        </NAlert>
        
        <!-- Recovery form -->
        <NForm>
          <NFormItem :label="getQuestionText(patientData.security_question_1)">
            <NInput
              v-model:value="recoveryAnswers.answer1"
              type="password"
              placeholder="Enter your answer"
              :disabled="isRecovering || isBlocked"
              show-password-on="click"
            />
          </NFormItem>
          
          <NFormItem :label="getQuestionText(patientData.security_question_2)">
            <NInput
              v-model:value="recoveryAnswers.answer2"
              type="password"
              placeholder="Enter your answer"
              :disabled="isRecovering || isBlocked"
              show-password-on="click"
            />
          </NFormItem>
          
          <NFormItem :label="getQuestionText(patientData.security_question_3)">
            <NInput
              v-model:value="recoveryAnswers.answer3"
              type="password"
              placeholder="Enter your answer"
              :disabled="isRecovering || isBlocked"
              show-password-on="click"
            />
          </NFormItem>
          
          <div v-if="recoveryError" class="recovery-error">
            <NAlert type="error" :title="recoveryError" closable />
          </div>
          
          <NButton 
            @click="startPasswordRecovery"
            :loading="isRecovering"
            :disabled="!recoveryAnswers.answer1.trim() || !recoveryAnswers.answer2.trim() || !recoveryAnswers.answer3.trim() || isBlocked"
            type="primary"
          >
            <template #icon>
              <NIcon>
                <Key />
              </NIcon>
            </template>
            Reset Password
          </NButton>
        </NForm>
      </div>
    </NCard>
  </div>
</template>

<style scoped>
.settings-container {
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
}

.settings-section {
  margin-bottom: 24px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px;
}

.recovery-description {
  margin-bottom: 16px;
  color: #666;
  font-size: 0.95rem;
}

.block-notice,
.attempts-warning,
.security-notice,
.verification-success {
  margin-bottom: 20px;
}

.password-modal-content {
  padding: 16px 0;
}

.password-error,
.recovery-error,
.error-message {
  margin-bottom: 16px;
}

.success-message {
  margin-bottom: 16px;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .settings-container {
    padding: 10px;
  }
}
</style>